import {
  BoxGeometry,
  BufferGeometry,
  CircleGeometry,
  Color,
  CylinderGeometry,
  Euler,
  type EulerOrder,
  Float32BufferAttribute,
  Matrix4,
  Quaternion,
  Shape,
  Vector2,
  Vector3,
} from 'three';
import type { EraLook, Paint } from '../../config/eras';
import { GeoBuilder } from '../geo';
import { mix, PAL } from '../palette';

/**
 * The toolkit every era car is built from: lofted, faceted hulls, decals that
 * hug them, and a vertex-coloured builder so a whole car body is one draw call.
 * Local frame: facing +x, left = -z, ground at y = 0.
 */

export type Side = -1 | 1;
export const SIDES: readonly Side[] = [-1, 1];
const WHITE = new Color(1, 1, 1);

const _q = new Quaternion();
const _e = new Euler();

/** Translate, rotate (Euler), scale: the same order as an Object3D's matrix. */
export function M(x: number, y: number, z: number, rx = 0, ry = 0, rz = 0, sx = 1, sy = sx, sz = sx, order: EulerOrder = 'XYZ'): Matrix4 {
  return new Matrix4().compose(new Vector3(x, y, z), _q.setFromEuler(_e.set(rx, ry, rz, order)), new Vector3(sx, sy, sz));
}

export const V = (x: number, y: number, z: number): Vector3 => new Vector3(x, y, z);

/** A frame at `pos` whose +z faces `normal` and whose +y leans toward `up` (grilles, flat decals). */
export function facing(pos: Vector3, normal: Vector3, up: Vector3 = V(0, 1, 0), sx = 1, sy = sx, sz = 1): Matrix4 {
  const z = normal.clone().normalize();
  const y = up.clone().addScaledVector(z, -up.dot(z)).normalize();
  const x = new Vector3().crossVectors(y, z);
  return new Matrix4().makeBasis(x.multiplyScalar(sx), y.multiplyScalar(sy), z.multiplyScalar(sz)).setPosition(pos);
}

/** Length and placement of a Y-axis cylinder spanning a → b. */
export function segment(a: Vector3, b: Vector3): { len: number; m: Matrix4 } {
  const d = b.clone().sub(a);
  const len = d.length();
  const q = new Quaternion().setFromUnitVectors(V(0, 1, 0), d.normalize());
  return { len, m: new Matrix4().compose(a.clone().add(b).multiplyScalar(0.5), q, V(1, 1, 1)) };
}

export function paintColor(p: Paint): Color {
  // German racing silver, mixed from the palette like everything else.
  return p === 'silver' ? mix('cream', 'asphalt', 0.4) : PAL[p].clone();
}

// ---------------------------------------------------------------------------
// Hulls
// ---------------------------------------------------------------------------

/** One cross-section of a lofted body. */
export interface Station {
  x: number;
  /** Half-width. */
  w: number;
  top: number;
  bot: number;
  /** Centre across the car (side pods that pinch in); defaults to the hull's. */
  zc?: number;
}

/**
 * A lofted, faceted shell: superellipse cross-sections (n = 2 is an ellipse,
 * higher is boxier) joined tail to nose. Between stations every ring vertex
 * moves linearly, so the surface there is the superellipse of the
 * interpolated section: decals can be wrapped onto it exactly.
 */
export class Hull {
  constructor(
    readonly st: Station[],
    readonly n = 2.4,
    readonly seg = 12,
    readonly zc = 0,
  ) {}

  /** The same shape on the other side of the car (for side pods). */
  mirrored(): Hull {
    return new Hull(
      this.st.map((s) => ({ ...s, zc: s.zc === undefined ? undefined : -s.zc })),
      this.n,
      this.seg,
      -this.zc,
    );
  }

  section(x: number): { w: number; top: number; bot: number; zc: number } {
    const st = this.st;
    let i = 0;
    while (i < st.length - 2 && x > st[i + 1].x) i++;
    const a = st[i];
    const b = st[i + 1];
    const t = Math.min(1, Math.max(0, (x - a.x) / (b.x - a.x)));
    const za = a.zc ?? this.zc;
    const zb = b.zc ?? this.zc;
    return { w: a.w + (b.w - a.w) * t, top: a.top + (b.top - a.top) * t, bot: a.bot + (b.bot - a.bot) * t, zc: za + (zb - za) * t };
  }

  /** z of the side surface at (x, y). */
  sideZ(x: number, y: number, side: Side): number {
    const s = this.section(x);
    const yc = (s.top + s.bot) / 2;
    const u = Math.min(1, Math.abs((y - yc) / ((s.top - s.bot) / 2)));
    return s.zc + side * s.w * Math.pow(1 - Math.pow(u, this.n), 1 / this.n);
  }

  /** y of the top surface at (x, z). */
  topY(x: number, z: number): number {
    const s = this.section(x);
    const u = Math.min(1, Math.abs((z - s.zc) / s.w));
    return (s.top + s.bot) / 2 + ((s.top - s.bot) / 2) * Math.pow(1 - Math.pow(u, this.n), 1 / this.n);
  }

  geometry(): BufferGeometry {
    const { st, n, seg } = this;
    const f = (u: number): number => Math.sign(u) * Math.pow(Math.abs(u), 2 / n);
    // Half-step offset: flat faces on top, bottom and sides (seg divisible by 4).
    const rings = st.map((s) => {
      const yc = (s.top + s.bot) / 2;
      const b = (s.top - s.bot) / 2;
      const zc = s.zc ?? this.zc;
      const ring: Vector3[] = [];
      for (let k = 0; k < seg; k++) {
        const t = ((k + 0.5) / seg) * Math.PI * 2;
        ring.push(V(s.x, yc + b * f(Math.sin(t)), zc + s.w * f(Math.cos(t))));
      }
      return ring;
    });
    const pos: number[] = [];
    const tri = (a: Vector3, b: Vector3, c: Vector3): void => {
      pos.push(a.x, a.y, a.z, b.x, b.y, b.z, c.x, c.y, c.z);
    };
    for (let i = 0; i < rings.length - 1; i++) {
      const A = rings[i];
      const B = rings[i + 1];
      for (let k = 0; k < seg; k++) {
        const k2 = (k + 1) % seg;
        tri(A[k], B[k], B[k2]);
        tri(A[k], B[k2], A[k2]);
      }
    }
    const cap = (ring: Vector3[], s: Station, back: boolean): void => {
      if (s.w < 0.03) return;
      const c = V(s.x, (s.top + s.bot) / 2, s.zc ?? this.zc);
      for (let k = 0; k < seg; k++) {
        const k2 = (k + 1) % seg;
        if (back) tri(c, ring[k], ring[k2]);
        else tri(c, ring[k2], ring[k]);
      }
    };
    cap(rings[0], st[0], true);
    cap(rings[rings.length - 1], st[st.length - 1], false);
    const g = new BufferGeometry();
    g.setAttribute('position', new Float32BufferAttribute(pos, 3));
    return g;
  }
}

// ---------------------------------------------------------------------------
// Geometry helpers
// ---------------------------------------------------------------------------

/** Split triangles (longest edge first) until no edge is longer than maxEdge, so pieces can bend onto a curved body. */
export function subdivide(geo: BufferGeometry, maxEdge: number): BufferGeometry {
  const src = geo.index ? geo.toNonIndexed() : geo;
  const P = src.getAttribute('position');
  const T = src.getAttribute('uv');
  const out: number[] = [];
  const outUv: number[] = [];
  const stack: number[][] = [];
  for (let i = 0; i < P.count; i += 3) {
    const t: number[] = [];
    for (let k = 0; k < 3; k++) t.push(P.getX(i + k), P.getY(i + k), P.getZ(i + k), T ? T.getX(i + k) : 0, T ? T.getY(i + k) : 0);
    stack.push(t);
  }
  const len = (t: number[], a: number, b: number): number => Math.hypot(t[a] - t[b], t[a + 1] - t[b + 1], t[a + 2] - t[b + 2]);
  while (stack.length) {
    const t = stack.pop()!;
    const e = [len(t, 0, 5), len(t, 5, 10), len(t, 10, 0)];
    const m = Math.max(e[0], e[1], e[2]);
    if (m <= maxEdge) {
      for (let k = 0; k < 3; k++) {
        out.push(t[k * 5], t[k * 5 + 1], t[k * 5 + 2]);
        outUv.push(t[k * 5 + 3], t[k * 5 + 4]);
      }
      continue;
    }
    // Cyclic relabel keeps the winding: (i0, i1) is the longest edge, i2 the opposite vertex.
    const j = e.indexOf(m);
    const i0 = j * 5;
    const i1 = ((j + 1) % 3) * 5;
    const i2 = ((j + 2) % 3) * 5;
    const v = (i: number): number[] => t.slice(i, i + 5);
    const mid = v(i0).map((x, k) => (x + t[i1 + k]) / 2);
    stack.push([...v(i0), ...mid, ...v(i2)], [...mid, ...v(i1), ...v(i2)]);
  }
  const g = new BufferGeometry();
  g.setAttribute('position', new Float32BufferAttribute(out, 3));
  if (T) g.setAttribute('uv', new Float32BufferAttribute(outUv, 2));
  return g;
}

function flipWinding(g: BufferGeometry): void {
  for (const name of ['position', 'uv']) {
    const a = g.getAttribute(name);
    if (!a) continue;
    const s = a.itemSize;
    const arr = a.array as Float32Array;
    for (let i = 0; i < a.count; i += 3) {
      for (let k = 0; k < s; k++) {
        const p = (i + 1) * s + k;
        const q = (i + 2) * s + k;
        const tmp = arr[p];
        arr[p] = arr[q];
        arr[q] = tmp;
      }
    }
  }
}

/** Wrap a flat piece drawn in the x-y plane (the car seen from its right) onto one side of a hull. */
export function wrapSide(geo: BufferGeometry, h: Hull, side: Side, lift = 0.01, maxEdge = 0.07): BufferGeometry {
  const g = subdivide(geo, maxEdge);
  const p = g.getAttribute('position');
  for (let i = 0; i < p.count; i++) p.setZ(i, h.sideZ(p.getX(i), p.getY(i), side) + side * lift);
  if (side < 0) {
    // Seen from the left the faces must still point out, and a number must not read mirrored.
    flipWinding(g);
    const uv = g.getAttribute('uv');
    if (uv) for (let i = 0; i < uv.count; i++) uv.setX(i, 1 - uv.getX(i));
  }
  return g;
}

/** Bend a piece built around height yRef so it follows a hull's top surface. */
export function drapeTop(geo: BufferGeometry, h: Hull, yRef: number, lift = 0.008, maxEdge = 0.07): BufferGeometry {
  const g = subdivide(geo, maxEdge);
  const p = g.getAttribute('position');
  for (let i = 0; i < p.count; i++) p.setY(i, p.getY(i) - yRef + h.topY(p.getX(i), p.getZ(i)) + lift);
  return g;
}

/** Both windings, so a thin sheet (windscreens) shows from either side with one single-sided material. */
export function twoSided(geo: BufferGeometry): BufferGeometry {
  const src = geo.index ? geo.toNonIndexed() : geo;
  const p = src.getAttribute('position');
  const out: number[] = [];
  for (let i = 0; i < p.count; i++) out.push(p.getX(i), p.getY(i), p.getZ(i));
  for (let i = 0; i < p.count; i += 3) for (const k of [0, 2, 1]) out.push(p.getX(i + k), p.getY(i + k), p.getZ(i + k));
  const g = new BufferGeometry();
  g.setAttribute('position', new Float32BufferAttribute(out, 3));
  return g;
}

/** A curved, raked windscreen standing on y = 0, centred at the origin. */
export function screenGeometry(width: number, height: number, bend: number, rake: number, seg = 8): BufferGeometry {
  const pos: number[] = [];
  const P = (i: number, top: boolean): number[] => {
    const u = (i / seg) * 2 - 1;
    return [-bend * u * u - (top ? Math.sin(rake) * height : 0), top ? Math.cos(rake) * height : 0, (u * width) / 2];
  };
  for (let i = 0; i < seg; i++) {
    const a = P(i, false);
    const b = P(i + 1, false);
    const c = P(i + 1, true);
    const d = P(i, true);
    pos.push(...a, ...b, ...c, ...a, ...c, ...d);
  }
  const g = new BufferGeometry();
  g.setAttribute('position', new Float32BufferAttribute(pos, 3));
  return twoSided(g);
}

/** A band running parallel to a hull's top edge, drawn side-on (for wrapSide). */
export function stripeShape(h: Hull, x0: number, x1: number, below: number, height: number, n = 18): Shape {
  const top: Vector2[] = [];
  for (let i = 0; i <= n; i++) {
    const x = x0 + ((x1 - x0) * i) / n;
    top.push(new Vector2(x, h.section(x).top - below));
  }
  return new Shape([...top, ...top.slice().reverse().map((p) => new Vector2(p.x, p.y - height))]);
}

// ---------------------------------------------------------------------------
// Colours and the builder
// ---------------------------------------------------------------------------

export interface CarColors {
  main: Color;
  accent: Color;
  ochre: Color;
  cream: Color;
  brown: Color;
  dark: Color;
  /** Dull steel (suspension, pipes, engine). */
  metal: Color;
  /** Polished bits: grilles, wire spokes, radiator shells. */
  bright: Color;
  brass: Color;
  glass: Color;
  lens: Color;
  visor: Color;
  skin: Color;
  suit: Color;
  tire: Color;
  /** The painted rings of grooved tyres. */
  groove: Color;
  rim: Color;
  disc: Color;
  leather: Color;
  helmet: Color;
  stripe: Color;
  scarf: Color;
  light: Color;
}

export function carColors(look: EraLook, ghost: boolean): CarColors {
  const main = paintColor(ghost ? look.ghostMain : look.main);
  const accent = paintColor(ghost ? look.ghostAccent : look.accent);
  return {
    main,
    accent,
    ochre: PAL.ochre.clone(),
    cream: PAL.cream.clone(),
    brown: PAL.brown.clone(),
    dark: PAL.brown.clone(),
    metal: mix('asphalt', 'cream', 0.25),
    bright: mix('cream', 'asphalt', 0.3),
    brass: mix('ochre', 'brown', 0.25),
    glass: mix('cream', 'asphalt', 0.3),
    lens: mix('cream', 'asphalt', 0.45),
    visor: mix('brown', 'asphalt', 0.3),
    skin: mix('terracotta', 'cream', 0.3),
    suit: PAL.cream.clone(),
    tire: PAL.brown.clone(),
    groove: mix('brown', 'asphalt', 0.4),
    rim: ghost ? accent.clone() : paintColor(look.rim ?? 'cream'),
    disc: mix('cream', 'asphalt', 0.22),
    leather: mix('brown', 'terracotta', 0.4),
    helmet: paintColor(look.helmetColor),
    stripe: paintColor(ghost ? look.ghostAccent : look.helmetStripe),
    scarf: paintColor(ghost ? look.ghostAccent : (look.scarfColor ?? 'red')),
    light: PAL.red.clone(),
  };
}

/** Accumulates one car: flat-coloured pieces into one mesh, number roundels into another. */
export class CarKit {
  readonly g = new GeoBuilder();
  readonly decals = new GeoBuilder(true);

  constructor(readonly c: CarColors) {}

  add(geo: BufferGeometry, color: Color, m?: Matrix4, smooth = false): void {
    this.g.add(geo, color, m, smooth);
  }

  hull(h: Hull, color: Color): Hull {
    this.add(h.geometry(), color);
    return h;
  }

  box(color: Color, x: number, y: number, z: number, sx: number, sy: number, sz: number, rx = 0, ry = 0, rz = 0): void {
    this.add(new BoxGeometry(sx, sy, sz), color, M(x, y, z, rx, ry, rz));
  }

  rod(a: Vector3, b: Vector3, r: number, color: Color, sides = 6): void {
    const { len, m } = segment(a, b);
    this.add(new CylinderGeometry(r, r, len, sides), color, m);
  }

  /** A flat piece drawn side-on, wrapped onto the hull's sides. */
  sideDecal(h: Hull, geo: BufferGeometry, color: Color, sides: readonly Side[] = SIDES, lift = 0.008): void {
    for (const s of sides) this.add(wrapSide(geo, h, s, lift), color);
  }

  sideRoundel(h: Hull, x: number, y: number, r: number, sides: readonly Side[] = SIDES, lift = 0.014): void {
    for (const s of sides) this.decals.add(wrapSide(new CircleGeometry(r, 28).translate(x, y, 0), h, s, lift), WHITE);
  }

  /** A roundel lying on the hull's top, reading upright from behind the car. */
  topRoundel(h: Hull, x: number, z: number, r: number, lift = 0.01): void {
    const flat = new Matrix4().makeBasis(V(0, 0, 1), V(1, 0, 0), V(0, 1, 0)).setPosition(x, 0, z);
    this.decals.add(drapeTop(new CircleGeometry(r, 28).applyMatrix4(flat), h, 0, lift, 0.05), WHITE);
  }

  flatRoundel(m: Matrix4, r: number): void {
    this.decals.add(new CircleGeometry(r, 28), WHITE, m);
  }
}
